import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { v4 as uuidv4 } from 'uuid';
import { AwsSdkService } from 'src/app/shared/services/aws-sdk.service';
// MPS-263 | Akarsh Singh | 2026-08-31
// A supplier invited by PO token completes the SAME KYC as one with a
// portal account, so this screen shares the rules rather than restating
// them: the country config, its validator and the consent wording all
// come from the one place the portal wizard uses.
import {
  KycIdentifierField,
  KYC_COUNTRY_CONFIG_VERSION,
  getKycCountryRules,
} from 'src/app/views/settings/kyc/supplier-kyc/kyc-country-config';
import {
  KycIdentifierProblem,
  validateKycIdentifiers,
  toIdentifierPayload,
} from 'src/app/views/settings/kyc/supplier-kyc/kyc-identifier-validator';
import {
  KYC_CONSENT_DECLARATION,
  KYC_CONSENT_TEXT_VERSION,
  isConsentStillValid,
} from 'src/app/views/settings/kyc/supplier-kyc/kyc-consent-config';
import { SettingsService } from 'src/app/views/settings/settings.service';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import {
  KycCertification,
  KycDocument,
  KycLookups,
  KycMaster,
  KycService,
} from 'src/app/views/settings/kyc/kyc.service';
import { PrivateFileService } from '../../../shared/services/private-file.service';


interface KycTab {
  key: 'org' | 'docs' | 'bank' | 'preview';
  label: string;
  icon: string;
}

interface DocSlot {
  documentTypeId: number;
  code: string;
  label: string;
  required: boolean;
  documentId: number | null;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
  pendingFile: File | null;
  /** MPS-263 KYC-103 — extra copies for a slot that takes several files. */
  moreFiles?: { documentId: number | null; fileName: string; fileUrl: string }[];
}

interface ExtraDoc {
  documentId: number | null;
  documentTypeId: number;
  name: string;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
  pendingFile: File | null;
}

interface CertOption {
  certificationId: number;
  code: string;
  title: string;
  subtitle: string;
  iconCode: string;
  isEnabled: boolean;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
  certificateNumber: string | null;
  issuer: string | null;
  validityDate: string | null;
  pendingFile: File | null;
}
@Component({
  selector: 'app-s-token-supplier-kyc',
  templateUrl: './s-token-supplier-kyc.component.html',
  styleUrls: ['./s-token-supplier-kyc.component.css']
})
export class STokenSupplierKycComponent implements OnInit {
  @Input() kycId!: number;
  @Input() supplierId!: number;
  @Input() kycReference: string = '';
  @Input() readOnly: boolean = false;
  @Input() presetLookups: KycLookups | null = null;
  @Input() applicantType: 'SUPPLIER' | 'BUYER' = 'SUPPLIER';

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<{ status: string }>();

  tabs: KycTab[] = [
    { key: 'org', label: 'Organizational Data', icon: 'business' },
    { key: 'docs', label: 'KYC Documents', icon: 'description' },
    { key: 'bank', label: 'Banking Information', icon: 'account_balance' },
    { key: 'preview', label: 'Preview & Submit', icon: 'fact_check' },
  ];

  activeTab: KycTab['key'] = 'org';
  showBankAccountNumber = false;

  // Lookups fetched from backend
  businessTypes: { id: number; displayName: string }[] = [];
  bankAccountTypes: { id: number; displayName: string }[] = [];
  documentTypes: { id: number; code: string; displayName: string; isRequired: number }[] = [];

  orgForm!: FormGroup;
  bankForm!: FormGroup;

  logoFile: File | null = null;
  logoFileName: string = '';
  logoPreview: string = '';
  logoUrl: string = '';

  documents: DocSlot[] = [];
  extraDocs: ExtraDoc[] = [];
  additionalDocumentTypeId: number | null = null;
  certifications: CertOption[] = [];

  loading: boolean = false;
  saving: boolean = false;
  tabSwitching: boolean = false;

  // Admin feedback / clarification surfaced to the applicant
  adminBannerKind: 'CLARIFICATION' | 'REJECTED' | 'APPROVED' | null = null;
  adminBannerMessage: string = '';
  adminBannerStatus: string = '';

  constructor(
    private privateFiles: PrivateFileService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private kycService: KycService,
    private awsSdkService: AwsSdkService,
    private webApiService: WebApiService,
    // MPS-263 KYC-101 — the portal's existing country list.
    private settingsService: SettingsService
  ) {}

  // ================================================================
  // MPS-263 | Akarsh Singh | 2026-08-31
  // A PO-token supplier must satisfy exactly the same submit gate as a
  // supplier in the portal, so this screen captures the same country
  // identifiers, bank details and declaration of truth. The rules
  // themselves are imported, never restated.
  // ================================================================
  countryList: any[] = [];
  selectedCountryId: number | null = null;
  selectedCountryCode: string | null = null;
  identifierFields: KycIdentifierField[] = [];
  identifierValues: Record<string, string> = {};
  identifierErrors: Record<string, string> = {};

  readonly consentDeclaration = KYC_CONSENT_DECLARATION;
  consentAccepted = false;

  private readonly MULTI_FILE_DOCUMENT_CODES = ['UBO_OWNER_IDS'];

  slotTakesManyFiles(slot: DocSlot): boolean {
    return this.MULTI_FILE_DOCUMENT_CODES.includes(slot.code);
  }

  get selectedCountryName(): string {
    const c = this.countryList.find(
      (x) => Number(x.id) === Number(this.selectedCountryId)
    );
    return c?.name || '';
  }

  get identifierProblems(): KycIdentifierProblem[] {
    return validateKycIdentifiers(this.selectedCountryCode, this.identifierValues);
  }

  get areIdentifiersValid(): boolean {
    return this.selectedCountryId != null && this.identifierProblems.length === 0;
  }

  get isConsentRequired(): boolean {
    return !this.consentAccepted;
  }

  private async loadCountries(): Promise<void> {
    try {
      const res: any = await this.settingsService.fetchAllCountryDetails();
      const rows = Array.isArray(res) ? res : (res?.rows || res?.data || []);
      // The list carries soft-deleted duplicates of a few countries.
      this.countryList = (rows || []).filter(
        (c: any) => Number(c?.isActive) === 1 && Number(c?.isDelete) !== 1
      );
    } catch {
      this.countryList = [];
    }
  }

  async onCountryChange(countryId: number | null): Promise<void> {
    const hasEnteredSomething = Object.values(this.identifierValues)
      .some((v) => !!(v || '').trim());
    if (this.selectedCountryId && hasEnteredSomething &&
        Number(countryId) !== Number(this.selectedCountryId)) {
      const proceed = window.confirm(
        'Changing the country clears the identifiers you have entered, ' +
        'because they only mean something in the country that issued them. Continue?'
      );
      if (!proceed) {
        this.orgForm.get('countryOfIncorporationId')
          ?.setValue(this.selectedCountryId, { emitEvent: false });
        return;
      }
    }
    this.applyCountry(countryId);
  }

  private applyCountry(countryId: number | null): void {
    const country = this.countryList.find((c) => Number(c.id) === Number(countryId));
    this.selectedCountryId = countryId ?? null;
    this.selectedCountryCode = country?.code ? String(country.code).trim() : null;
    this.identifierFields = getKycCountryRules(this.selectedCountryCode).fields;
    this.identifierValues = {};
    this.identifierErrors = {};
  }

  onIdentifierBlur(fieldKey: string): void {
    const problem = this.identifierProblems.find((p) => p.fieldKey === fieldKey);
    if (problem) this.identifierErrors[fieldKey] = problem.message;
    else delete this.identifierErrors[fieldKey];
  }

  showAllIdentifierProblems(): void {
    this.identifierErrors = {};
    for (const problem of this.identifierProblems) {
      this.identifierErrors[problem.fieldKey] = problem.message;
    }
  }

  private restoreIdentifiers(master: any, identifiers: any[]): void {
    this.applyCountry(master?.countryOfIncorporationId ?? null);
    for (const row of identifiers || []) {
      if (row?.fieldKey) this.identifierValues[row.fieldKey] = row.fieldValue ?? '';
    }
    this.consentAccepted = isConsentStillValid(master?.consentTextVersion ?? null);
  }

  /** MPS-263 KYC-103 — hand the supplier the blank declaration form. */
  async downloadUboTemplate(): Promise<void> {
    try {
      const blob = await this.kycService.downloadUboDeclarationTemplate('SERVICE_TOKEN');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'UBO-Declaration-Template.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      this.snackBar.open(
        error?.error?.message || 'Could not download the declaration form',
        'OK', { duration: 3000 }
      );
    }
  }

  /** MPS-263 KYC-105 — one message naming everything that is missing. */
  private describeWhatIsMissing(): string {
    const missing: string[] = [];
    if (!this.isOrgComplete()) missing.push('Organization details');
    for (const label of this.getMissingDocs()) missing.push(label);
    if (!this.isBankComplete()) missing.push('Banking details');
    if (this.isConsentRequired) missing.push('Declaration of truth');
    return missing.length
      ? `Please complete the following before submitting: ${missing.join('; ')}`
      : 'Please complete all required fields and documents before submitting.';
  }

  /**
   * Returns the active KYC service for the current applicant type. Both
   * services expose the same shape so call sites stay identical.
   */
  private get api(): any {
    return this.kycService;
  }

  /**
   * Upload a file to S3 using the project's presigned-URL pattern.
   * Returns the canonical S3 location (no query string) on success or null
   * on failure. The folder lets us organize uploads by purpose.
   */
  /**
   * Generate a presigned GET URL for viewing/downloading an existing S3 file.
   * Returns null if the URL can't be generated.
   */
  private async fetchSignedViewUrl(s3Path: string): Promise<string | null> {
    try {
      if (!s3Path) return null;
      // If it's already a fully-qualified URL with scheme, return as-is.
      if (/^https?:\/\//i.test(s3Path)) return s3Path;
      // 2026-10-25 (S3 step 2): a presigned GET from the owner-checked endpoint (getPreSignedUrl only signs PUTs).
      const signed = await this.privateFiles.signedUrl(s3Path);
      return signed || null;
    } catch (error) {
      console.warn('[KYC][wizard] could not fetch signed view URL', error);
      return null;
    }
  }

  /** Open an existing uploaded file in a new tab via a fresh presigned URL. */
  async viewFile(s3Path: string | null | undefined): Promise<void> {
    if (!s3Path) return;
    const signed = await this.fetchSignedViewUrl(s3Path);
    if (signed) window.open(signed, '_blank');
  }

  private async uploadFileToS3(
    file: File,
    folder: string
  ): Promise<string | null> {
    try {
      const ext = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2);
      const fileName = `${uuidv4()}.${(ext || 'bin').toLowerCase()}`;
      const s3Location = `${folder}/${fileName}`;
      const presigned = await this.awsSdkService.getSignedUrlPromise({
        fileKey: s3Location,
        fileContentType: file.type,
      });
      if (!presigned) {
        this.snackBar.open('Could not get upload URL', 'OK', { duration: 2500 });
        return null;
      }
      await this.webApiService.putRequestExternalApi(presigned, file);
      return this.awsSdkService.extractS3LocationFromUrl(presigned);
    } catch (error: any) {
      console.error('[KYC][wizard] S3 upload failed:', error);
      this.snackBar.open(
        error?.error?.message || error?.message || 'File upload failed',
        'OK',
        { duration: 3000 }
      );
      return null;
    }
  }

  ngOnInit(): void {
    this.buildForms();
    if (this.readOnly) {
      this.orgForm.disable({ emitEvent: false });
      this.bankForm.disable({ emitEvent: false });
    }
    // Render fallback document slots and certifications immediately so the
    // UI is never blank while the lookup call is in flight.
    this.applyLookups({
      businessTypes: [],
      bankAccountTypes: [],
      documentTypes: [],
      certifications: [],
    } as KycLookups);
    this.bootstrap();
  }

  /* -------------------- Bootstrap -------------------- */

  async bootstrap(): Promise<void> {
    this.loading = true;
    try {
      // Prefer preset lookups passed by the dashboard so dropdowns
      // appear immediately on first paint. Fall back to fetching only
      // if the parent didn't pre-load them.
      let lookups: any = this.presetLookups;
      if (!lookups) {
        const raw: any = await this.api.getLookupsByServicePoToken();
        lookups = raw?.businessTypes ? raw : raw?.data?.businessTypes ? raw.data : raw;
      }
      console.log('[KYC][wizard] applying lookups:', {
        businessTypes: lookups?.businessTypes?.length || 0,
        bankAccountTypes: lookups?.bankAccountTypes?.length || 0,
        documentTypes: lookups?.documentTypes?.length || 0,
        certifications: lookups?.certifications?.length || 0,
      });
      this.applyLookups(lookups as KycLookups);

      // MPS-263 KYC-101 — the dropdown must exist before the stored country
      // can be resolved back onto it.
      await this.loadCountries();

      const appRaw: any = await this.api.getApplicationByServicePoToken(this.kycId, this.supplierId);
      const app = appRaw?.master !== undefined ? appRaw : appRaw?.data ?? appRaw;
      console.log('[KYC][wizard] application loaded:', app);
      this.applyApplication(app.master, app.documents || [], app.certifications || []);
      // MPS-263 KYC-101 / KYC-104
      this.restoreIdentifiers(app.master, app.identifiers || []);
    } catch (error: any) {
      console.error('[KYC][wizard] bootstrap error:', error);
      this.snackBar.open(
        error?.error?.message || error?.message || 'Failed to load KYC data',
        'OK',
        { duration: 3000 }
      );
    } finally {
      this.loading = false;
    }
  }

  applyLookups(lookups: KycLookups): void {
    this.businessTypes = (lookups?.businessTypes || []).map((b) => ({
      id: b.id,
      displayName: b.displayName,
    }));
    this.bankAccountTypes = (lookups?.bankAccountTypes || []).map((b) => ({
      id: b.id,
      displayName: b.displayName,
    }));
    this.documentTypes = lookups?.documentTypes || [];

    // Document slot definitions — render UI from backend lookup if available,
    // otherwise fall back to the canonical 4 mandatory slots so the UI never
    // looks empty while we wait for the lookup call to finish.
    const additional = this.documentTypes.find((d) => d.code === 'ADDITIONAL');
    this.additionalDocumentTypeId = additional ? additional.id : null;

    if (this.documentTypes.length > 0) {
      this.documents = this.documentTypes
        .filter((d) => d.code !== 'ADDITIONAL')
        .map((d) => ({
          documentTypeId: d.id,
          code: d.code,
          label: d.displayName,
          required: !!d.isRequired,
          documentId: null,
          fileUrl: null,
          fileName: null,
          fileSize: null,
          fileMimeType: null,
          pendingFile: null,
          moreFiles: [],
        }));
    } else {
      this.documents = this.fallbackDocumentSlots();
    }

    if (lookups?.certifications && lookups.certifications.length > 0) {
      this.certifications = lookups.certifications.map((c) => ({
        certificationId: c.id,
        code: c.code,
        title: c.title,
        subtitle: c.subtitle,
        iconCode: c.iconCode,
        isEnabled: false,
        fileUrl: null,
        fileName: null,
        fileSize: null,
        fileMimeType: null,
        certificateNumber: null,
        issuer: null,
        validityDate: null,
        pendingFile: null,
      }));
    } else {
      this.certifications = this.fallbackCertifications();
    }
  }

  private fallbackDocumentSlots(): DocSlot[] {
    const make = (code: string, label: string, required: boolean): DocSlot => ({
      documentTypeId: 0, code, label, required,
      documentId: null, fileUrl: null, fileName: null, fileSize: null,
      fileMimeType: null, pendingFile: null,
    });
    return [
      make('LEGAL_ENTITY_REGISTRATION', 'Legal Entity Registration', true),
      make('TAX_IDENTIFICATION', 'Tax Identification Document', true),
      make('PROOF_OF_ADDRESS', 'Proof of Address', true),
      make('CERTIFICATIONS', 'Certifications (ISO, etc.)', false),
    ];
  }

  private fallbackCertifications(): CertOption[] {
    const base = [
      ['ISO_9001', 'ISO 9001:2015 — Quality Management', 'Cert no · issuer · validity – upload on next step', 'verified'],
      ['ISO_14001', 'ISO 14001 — Environmental Management', 'Required for bunker / chemical / paint suppliers', 'eco'],
      ['ISO_45001', 'ISO 45001 / OHSAS — Occupational Safety', 'Recommended for ship-repair suppliers', 'health_and_safety'],
      ['ISM_CODE', 'ISM Code compliance letter', 'International Safety Management (SOLAS Ch. IX)', 'assignment_turned_in'],
      ['ISPS_CODE', 'ISPS Code — port facility / supplier security', 'Mandatory for port-access suppliers', 'security'],
      ['MARPOL', 'MARPOL Annex I / V handler permit', 'Oil / garbage suppliers only', 'water_drop'],
      ['CLASS_SOCIETY', 'Class-society approval (DNV · LR · ABS · IRS · BV · NK)', 'Required for marine equipment & spares suppliers', 'workspace_premium'],
      ['OCIMF_SIRE_TMSA', 'OCIMF / SIRE / TMSA compliance (tanker sector)', 'Only for bunker & tanker-service suppliers', 'verified_user'],
    ];
    return base.map(([code, title, subtitle, iconCode]) => ({
      certificationId: 0, code, title, subtitle, iconCode,
      isEnabled: false, fileUrl: null, fileName: null, fileSize: null, fileMimeType: null,
      certificateNumber: null, issuer: null, validityDate: null, pendingFile: null,
    }));
  }

  applyApplication(master: KycMaster, docs: KycDocument[], certs: KycCertification[]): void {
    // Reset variable-length collections so a second bootstrap (e.g. reopen)
    // doesn't duplicate rows.
    this.extraDocs = [];
    for (const slot of this.documents) {
      slot.documentId = null;
      slot.fileUrl = null;
      slot.fileName = null;
      slot.fileSize = null;
      slot.fileMimeType = null;
      slot.pendingFile = null;
    }
    for (const cert of this.certifications) {
      cert.isEnabled = false;
      cert.fileUrl = null;
      cert.fileName = null;
      cert.fileSize = null;
      cert.fileMimeType = null;
      cert.certificateNumber = null;
      cert.issuer = null;
      cert.validityDate = null;
      cert.pendingFile = null;
    }

    if (master) {
      const mm: any = master;
      this.orgForm.patchValue({
        organizationName: master.organizationName ?? '',
        businessTypeId: master.businessTypeId ?? '',
        // Structured address parts returned inline by GET_FULL (joined from
        // the KYC-owned addressDetails rows).
        registeredAddress: {
          street: mm.registeredStreet ?? '',
          city: mm.registeredCity ?? '',
          region: mm.registeredRegion ?? '',
          country: mm.registeredCountry ?? '',
          zipcode: mm.registeredZipcode ?? '',
          countryCode: mm.registeredCountryCode ?? '',
          stateCode: mm.registeredStateCode ?? '',
          countryId: mm.registeredCountryId ?? null,
        },
        tradingAddress: {
          street: mm.tradingStreet ?? '',
          city: mm.tradingCity ?? '',
          region: mm.tradingRegion ?? '',
          country: mm.tradingCountry ?? '',
          zipcode: mm.tradingZipcode ?? '',
          countryCode: mm.tradingCountryCode ?? '',
          stateCode: mm.tradingStateCode ?? '',
          countryId: mm.tradingCountryId ?? null,
        },
        email: master.email ?? '',
        phone: master.phone ?? '',
        website: master.website ?? '',
        registrationNumber: master.businessRegistrationNumber ?? '',
        taxId: master.taxId ?? '',
        countryOfIncorporationId: (master as any).countryOfIncorporationId ?? null,
      });
      this.bankForm.patchValue({
        accountHolderName: master.accountHolderName ?? '',
        // MPS-263 KYC-102
        bankName: (master as any).bankName ?? '',
        branchName: (master as any).branchName ?? '',
        accountNumber: master.bankAccountNumberEncrypted ?? '',
        confirmAccountNumber: master.bankAccountNumberEncrypted ?? '',
        ifscSwift: master.ifscSwiftCode ?? '',
        accountTypeId: master.bankAccountTypeId ?? '',
      });
      this.logoUrl = master.logoUrl || '';
      this.logoPreview = '';
      if (master.logoUrl) {
        this.fetchSignedViewUrl(master.logoUrl).then((signed) => {
          if (signed) this.logoPreview = signed;
        });
      }

      // ===== Admin banner (clarification / rejection / approval) =====
      const m: any = master;
      this.adminBannerStatus = m.status || '';
      if (m.status === 'CLARIFICATION_REQUIRED' && m.clarificationNote) {
        this.adminBannerKind = 'CLARIFICATION';
        this.adminBannerMessage = m.clarificationNote;
      } else if (m.status === 'REJECTED' && m.adminFeedback) {
        this.adminBannerKind = 'REJECTED';
        this.adminBannerMessage = m.adminFeedback;
      } else if (m.status === 'APPROVED') {
        this.adminBannerKind = 'APPROVED';
        this.adminBannerMessage = '';
      } else {
        this.adminBannerKind = null;
        this.adminBannerMessage = '';
      }
    }

    // Apply mandatory document slots from existing rows
    for (const doc of docs) {
      if (doc.isAdditional === 1) {
        this.extraDocs.push({
          documentId: doc.id,
          documentTypeId: doc.documentTypeId || (this.additionalDocumentTypeId as number),
          name: doc.documentName || '',
          fileUrl: doc.fileUrl,
          fileName: doc.documentName || this.extractFileName(doc.fileUrl),
          fileSize: doc.fileSizeBytes,
          fileMimeType: doc.fileMimeType,
          pendingFile: null,
        });
      } else {
        const slot = this.documents.find((s) => s.documentTypeId === doc.documentTypeId);
        if (slot && this.slotTakesManyFiles(slot)) {
          // MPS-263 KYC-103: several rows share this type, one per owner.
          slot.moreFiles = slot.moreFiles || [];
          slot.moreFiles.push({
            documentId: doc.id,
            fileName: doc.documentName || this.extractFileName(doc.fileUrl),
            fileUrl: doc.fileUrl,
          });
          slot.fileUrl = doc.fileUrl;
        } else if (slot) {
          slot.documentId = doc.id;
          slot.fileUrl = doc.fileUrl;
          slot.fileName = this.extractFileName(doc.fileUrl);
          slot.fileSize = doc.fileSizeBytes;
          slot.fileMimeType = doc.fileMimeType;
        }
      }
    }

    // Apply existing certification selections
    for (const c of certs) {
      const opt = this.certifications.find((x) => x.certificationId === c.certificationId);
      if (opt) {
        opt.isEnabled = c.isEnabled === 1;
        opt.fileUrl = c.fileUrl;
        opt.fileName = c.fileUrl ? this.extractFileName(c.fileUrl) : null;
        opt.fileSize = c.fileSizeBytes;
        opt.fileMimeType = c.fileMimeType;
        opt.certificateNumber = c.certificateNumber;
        opt.issuer = c.issuer;
        opt.validityDate = c.validityDate;
      }
    }
  }

  /* -------------------- Forms -------------------- */

  /**
   * Build a structured address sub-group (country / region / city / street /
   * pincode). When `required` is true the key fields are mandatory (used for
   * the registered address); the trading address is fully optional.
   *
   * countryCode / stateCode / countryId are hidden round-trip fields: they're
   * seeded from the prefill (the cloned company address carries them) and sent
   * back unchanged on save, so editing the visible text fields never wipes the
   * ISO codes the company address already had.
   */
  private buildAddressGroup(required: boolean): FormGroup {
    return this.fb.group({
      street:  ['', required ? [Validators.required, Validators.maxLength(500)] : [Validators.maxLength(500)]],
      city:    ['', required ? [Validators.required, Validators.maxLength(50)]  : [Validators.maxLength(50)]],
      region:  ['', [Validators.maxLength(50)]],
      country: ['', required ? [Validators.required, Validators.maxLength(50)]  : [Validators.maxLength(50)]],
      zipcode: ['', required ? [Validators.required, Validators.maxLength(20)]  : [Validators.maxLength(20)]],
      // Hidden, round-tripped from prefill — not shown in the form.
      countryCode: [''],
      stateCode: [''],
      countryId: [null],
    });
  }

  buildForms(): void {
    this.orgForm = this.fb.group({
      organizationName: ['', [Validators.required, Validators.maxLength(150)]],
      businessTypeId: ['', Validators.required],
      // Structured addresses (replaces the old free-text blobs).
      registeredAddress: this.buildAddressGroup(true),
      tradingAddress: this.buildAddressGroup(false),
      // Email is the login username — seeded from the account, never editable.
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[+0-9\-()\s]{6,20}$/)]],
      website: ['', this.optionalUrlValidator()],
      // MPS-263 KYC-101: kept so existing records still round-trip, but the
      // country-specific identifiers below replace them on screen.
      registrationNumber: ['', [Validators.maxLength(60)]],
      taxId: ['', [Validators.maxLength(60)]],
      // The country decides which identifiers are asked for.
      countryOfIncorporationId: [null, Validators.required],
    });

    this.bankForm = this.fb.group(
      {
        accountHolderName: ['', [Validators.required, Validators.maxLength(150)]],
        // MPS-263 KYC-102 — read off the uploaded bank proof by the admin.
        bankName: ['', [Validators.required, Validators.maxLength(255)]],
        branchName: ['', [Validators.required, Validators.maxLength(255)]],
        accountNumber: ['', [Validators.required, Validators.maxLength(34)]],
        confirmAccountNumber: ['', Validators.required],
        ifscSwift: ['', [Validators.required, Validators.maxLength(20)]],
        accountTypeId: ['', Validators.required],
      },
      { validators: this.matchAccountNumbersValidator }
    );
  }

  optionalUrlValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = (control.value ?? '').trim();
      if (!v) return null;
      const re = /^(https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,}([/?#].*)?$/i;
      return re.test(v) ? null : { invalidUrl: true };
    };
  }

  matchAccountNumbersValidator = (group: AbstractControl): ValidationErrors | null => {
    const a = group.get('accountNumber')?.value;
    const b = group.get('confirmAccountNumber')?.value;
    if (!a || !b) return null;
    return a === b ? null : { accountNumberMismatch: true };
  };

  /* -------------------- Tabs -------------------- */

  async switchTab(key: KycTab['key']): Promise<void> {
    if (key === this.activeTab) return;
    await this.persistCurrentTabSilently(key);
    this.activeTab = key;
  }

  async goNextTab(): Promise<void> {
    const idx = this.tabs.findIndex((t) => t.key === this.activeTab);
    if (idx < this.tabs.length - 1) {
      const nextKey = this.tabs[idx + 1].key;
      await this.persistCurrentTabSilently(nextKey);
      this.activeTab = nextKey;
    }
  }

  async goPrevTab(): Promise<void> {
    const idx = this.tabs.findIndex((t) => t.key === this.activeTab);
    if (idx > 0) {
      const prevKey = this.tabs[idx - 1].key;
      await this.persistCurrentTabSilently(prevKey);
      this.activeTab = prevKey;
    }
  }

  // Silent persistence triggered when the supplier moves between tabs.
  // Only persists the tab the supplier is leaving, only if it has any input,
  // and never throws — failures are logged but never block navigation.
  private async persistCurrentTabSilently(nextTabKey: KycTab['key']): Promise<void> {
    if (this.readOnly || this.loading || this.saving) return;
    let didStartLoading = false;
    try {
      if (
        this.activeTab === 'org' ||
        this.activeTab === 'bank' ||
        this.activeTab === 'docs'
      ) {
        this.tabSwitching = true;
        didStartLoading = true;
      }
      if (this.activeTab === 'docs') {
        await this.flushPendingDocuments();
      }
      if (this.activeTab === 'org') {
        // getRawValue so the disabled email control is included in the payload.
        const o = this.orgForm.getRawValue();
        if (
          o.organizationName || o.email || o.phone || o.taxId ||
          o.businessTypeId || o.registrationNumber
        ) {
          await this.api.saveOrgByServicePoToken({
            kycId: this.kycId,
            organizationName: o.organizationName,
            businessTypeId: o.businessTypeId || null,
            // Structured address parts (the SP upserts the KYC-owned rows).
            registeredAddressParts: o.registeredAddress,
            tradingAddressParts: o.tradingAddress,
            logoUrl: this.logoUrl || null,
            email: o.email,
            phone: o.phone,
            website: o.website,
            businessRegistrationNumber: o.registrationNumber,
            taxId: o.taxId,
            // MPS-263 KYC-101
            countryOfIncorporationId: this.selectedCountryId,
            identifiers: toIdentifierPayload(this.selectedCountryCode, this.identifierValues),
            configVersion: KYC_COUNTRY_CONFIG_VERSION,
            currentStep: nextTabKey.toUpperCase(),
          });

          // Saved separately so a country change clears what no longer applies.
          await this.api.saveIdentifiersByServicePoToken({
            kycId: this.kycId,
            supplierId: this.supplierId,
            countryOfIncorporationId: this.selectedCountryId,
            identifiers: toIdentifierPayload(this.selectedCountryCode, this.identifierValues),
            configVersion: KYC_COUNTRY_CONFIG_VERSION,
          });
        }
      } else if (this.activeTab === 'bank') {
        const b = this.bankForm.value;
        if (b.accountHolderName || b.accountNumber || b.ifscSwift || b.accountTypeId) {
          await this.api.saveBankingByServicePoToken({
            kycId: this.kycId,
            accountHolderName: b.accountHolderName,
            bankAccountNumber: b.accountNumber,
            ifscSwiftCode: b.ifscSwift,
            // MPS-263 KYC-102
            bankName: b.bankName,
            branchName: b.branchName,
            bankAccountTypeId: b.accountTypeId || null,
            currentStep: nextTabKey.toUpperCase(),
          });
        }
      }
      // 'docs' and 'preview' have no aggregate form to save — docs/certs are
      // already persisted per-action when the supplier uploads or toggles.
    } catch (error: any) {
      console.warn('[KYC][wizard] silent tab-save failed:', error?.message || error);
    } finally {
      if (didStartLoading) this.tabSwitching = false;
    }
  }

  /* -------------------- Logo upload (placeholder) -------------------- */

  async onLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      this.snackBar.open('Logo must be PNG or JPEG', 'OK', { duration: 3000 });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.snackBar.open('Logo must be 2MB or smaller', 'OK', { duration: 3000 });
      return;
    }
    this.logoFile = file;
    this.logoFileName = file.name;
    const reader = new FileReader();
    reader.onload = () => (this.logoPreview = reader.result as string);
    reader.readAsDataURL(file);

    const folder = `kyc/${this.supplierId}/logo`;
    const s3Url = await this.uploadFileToS3(file, folder);
    if (s3Url) {
      this.logoUrl = s3Url;
      // Persist the logo URL on the master row immediately.
      try {
        await this.api.saveOrgByServicePoToken({
          kycId: this.kycId,
          logoUrl: this.logoUrl,
          currentStep: this.activeTab.toUpperCase(),
        });
      } catch (error: any) {
        console.warn('[KYC][wizard] persist logo URL failed:', error?.message);
      }
    }
  }

  removeLogo(): void {
    this.logoFile = null;
    this.logoFileName = '';
    this.logoPreview = '';
    this.logoUrl = '';
  }

  /* -------------------- Document upload -------------------- */

  validateDocFile(file: File): boolean {
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) {
      this.snackBar.open('Only PDF, JPEG and PNG files are allowed', 'OK', { duration: 3000 });
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.snackBar.open('File must be 10MB or smaller', 'OK', { duration: 3000 });
      return false;
    }
    return true;
  }

  onDocSelected(event: Event, slot: DocSlot): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.validateDocFile(file)) return;
    this.persistSlot(slot, file);
  }

  onDocDropped(event: DragEvent, slot: DocSlot): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file || !this.validateDocFile(file)) return;
    this.persistSlot(slot, file);
  }

  onDocDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  // Stage the file locally (no upload, no DB save). Actual upload + save
  // happens when the supplier leaves the Documents tab via flushPendingDocuments().
  persistSlot(slot: DocSlot, file: File): void {
    if (this.readOnly) return;
    slot.pendingFile = file;
    slot.fileName = file.name;
    slot.fileSize = file.size;
    slot.fileMimeType = file.type;
  }

  async removeDoc(slot: DocSlot): Promise<void> {
    if (this.readOnly) return;
    // Pending-only file (not yet uploaded) → just drop locally.
    if (!slot.documentId) {
      slot.pendingFile = null;
      slot.fileUrl = null;
      slot.fileName = null;
      slot.fileSize = null;
      slot.fileMimeType = null;
      return;
    }
    try {
      await this.api.saveDocumentByServicePoToken({
        kycId: this.kycId,
        documentId: slot.documentId,
        documentTypeId: slot.documentTypeId,
        docOpMode: 'DELETE',
      });
      slot.documentId = null;
      slot.pendingFile = null;
      slot.fileUrl = null;
      slot.fileName = null;
      slot.fileSize = null;
      slot.fileMimeType = null;
    } catch (error: any) {
      this.snackBar.open(error?.error?.message || 'Failed to remove document', 'OK', {
        duration: 3000,
      });
    }
  }

  /* -------------------- Extra documents -------------------- */

  addExtraDoc(): void {
    if (this.readOnly) return;
    if (!this.additionalDocumentTypeId) return;
    this.extraDocs.push({
      documentId: null,
      documentTypeId: this.additionalDocumentTypeId,
      name: '',
      fileUrl: null,
      fileName: null,
      fileSize: null,
      fileMimeType: null,
      pendingFile: null,
    });
  }

  async removeExtraDoc(index: number): Promise<void> {
    const doc = this.extraDocs[index];
    if (doc?.documentId) {
      try {
        await this.api.saveDocumentByServicePoToken({
          kycId: this.kycId,
          documentId: doc.documentId,
          documentTypeId: doc.documentTypeId,
          docOpMode: 'DELETE',
        });
      } catch {
        /* ignore */
      }
    }
    this.extraDocs.splice(index, 1);
  }

  onExtraDocFileSelected(event: Event, doc: ExtraDoc): void {
    if (this.readOnly) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.validateDocFile(file)) return;
    doc.pendingFile = file;
    doc.fileName = file.name;
    doc.fileSize = file.size;
    doc.fileMimeType = file.type;
    if (!doc.name) doc.name = file.name;
  }

  /* -------------------- Certifications -------------------- */

  toggleCertification(cert: CertOption): void {
    if (this.readOnly) return;
    const previous = cert.isEnabled;
    const next = !previous;

    // Optimistic UI: flip instantly so the supplier sees immediate feedback.
    cert.isEnabled = next;
    if (!next) {
      cert.fileUrl = null;
      cert.fileName = null;
      cert.fileSize = null;
      cert.fileMimeType = null;
      cert.pendingFile = null;
    }

    // Persist in the background. If it fails, revert the visual state.
    this.api
      .saveCertification({
        kycId: this.kycId,
        certificationId: cert.certificationId,
        isEnabled: next,
      })
      .catch((error: any) => {
        cert.isEnabled = previous;
        this.snackBar.open(
          error?.error?.message || 'Failed to update certification',
          'OK',
          { duration: 3000 }
        );
      });
  }

  onCertFileSelected(event: Event, cert: CertOption): void {
    if (this.readOnly) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.validateDocFile(file)) return;
    cert.pendingFile = file;
    cert.fileName = file.name;
    cert.fileSize = file.size;
    cert.fileMimeType = file.type;
    cert.isEnabled = true; // attaching evidence = claiming the cert
  }

  /* -------------------- Batch flush for pending uploads -------------------- */

  /**
   * Walks every document slot, additional doc, and certification with a
   * pending File and uploads them in parallel to S3, then persists each via
   * a single saveDocument / saveCertification call. Designed to be called
   * once when the supplier leaves the Documents tab or hits Submit.
   */
  async flushPendingDocuments(): Promise<void> {
    if (this.readOnly) return;

    const tasks: Promise<void>[] = [];

    // Mandatory document slots
    for (const slot of this.documents) {
      if (!slot.pendingFile || !slot.documentTypeId) continue;
      tasks.push(this.uploadAndSaveDoc(slot, false));
    }
    // Additional documents
    for (const doc of this.extraDocs) {
      if (!doc.pendingFile || !doc.documentTypeId) continue;
      tasks.push(this.uploadAndSaveExtraDoc(doc));
    }
    // Certification files
    for (const cert of this.certifications) {
      if (!cert.pendingFile || !cert.certificationId) continue;
      tasks.push(this.uploadAndSaveCert(cert));
    }

    if (tasks.length === 0) return;
    await Promise.all(tasks);
  }

  private async uploadAndSaveDoc(slot: DocSlot, _additional: boolean): Promise<void> {
    const file = slot.pendingFile!;
    const folder = `kyc/${this.kycId}/documents/${slot.code}`;
    const fileUrl = await this.uploadFileToS3(file, folder);
    if (!fileUrl) return;
    try {
      const res: any = await this.api.saveDocumentByServicePoToken({
        kycId: this.kycId,
        documentId: slot.documentId,
        documentTypeId: slot.documentTypeId,
        documentName: file.name,
        fileUrl,
        fileSizeBytes: file.size,
        fileMimeType: file.type,
        isAdditional: 0,
        docOpMode: 'UPSERT',
        currentStep: 'DOCS',
      });
      if (this.slotTakesManyFiles(slot)) {
        // MPS-263 KYC-103: each file is its own row, so the slot records what
        // was added and clears itself, ready for the next owner.
        slot.moreFiles = slot.moreFiles || [];
        slot.moreFiles.push({
          documentId: res?.documentId ?? null,
          fileName: file.name,
          fileUrl,
        });
        slot.documentId = null;
        slot.fileUrl = fileUrl;
        slot.fileName = null;
        slot.pendingFile = null;
        return;
      }
      if (res?.documentId) slot.documentId = res.documentId;
      slot.fileUrl = fileUrl;
      slot.pendingFile = null;
    } catch (error: any) {
      console.warn('[KYC][wizard] save document failed:', error?.message);
    }
  }

  private async uploadAndSaveExtraDoc(doc: ExtraDoc): Promise<void> {
    const file = doc.pendingFile!;
    const folder = `kyc/${this.kycId}/documents/additional`;
    const fileUrl = await this.uploadFileToS3(file, folder);
    if (!fileUrl) return;
    try {
      const res: any = await this.api.saveDocumentByServicePoToken({
        kycId: this.kycId,
        documentId: doc.documentId,
        documentTypeId: doc.documentTypeId,
        documentName: doc.name || file.name,
        fileUrl,
        fileSizeBytes: file.size,
        fileMimeType: file.type,
        isAdditional: 1,
        docOpMode: 'UPSERT',
      });
      if (res?.documentId) doc.documentId = res.documentId;
      doc.fileUrl = fileUrl;
      doc.pendingFile = null;
    } catch (error: any) {
      console.warn('[KYC][wizard] save extra document failed:', error?.message);
    }
  }

  private async uploadAndSaveCert(cert: CertOption): Promise<void> {
    const file = cert.pendingFile!;
    const folder = `kyc/${this.kycId}/certifications/${cert.code}`;
    const fileUrl = await this.uploadFileToS3(file, folder);
    if (!fileUrl) return;
    try {
      await this.api.saveCertificationByServicePoToken({
        kycId: this.kycId,
        certificationId: cert.certificationId,
        isEnabled: true,
        fileUrl,
        fileSizeBytes: file.size,
        fileMimeType: file.type,
      });
      cert.fileUrl = fileUrl;
      cert.pendingFile = null;
    } catch (error: any) {
      console.warn('[KYC][wizard] save certification file failed:', error?.message);
    }
  }

  /* -------------------- Helpers -------------------- */

  getMissingDocs(): string[] {
    return this.documents.filter((d) => d.required && !d.fileUrl).map((d) => d.label);
  }

  isOrgComplete(): boolean {
    // MPS-263 KYC-101 — the section is only complete when the country
    // identifiers are filled and correctly formatted, as well as the form.
    return (this.orgForm?.valid ?? false) && this.areIdentifiersValid;
  }

  isDocsComplete(): boolean {
    return this.getMissingDocs().length === 0;
  }

  isBankComplete(): boolean {
    return this.bankForm?.valid ?? false;
  }

  isFormReadyToSubmit(): boolean {
    return (
      this.isOrgComplete() &&
      this.isDocsComplete() &&
      this.isBankComplete() &&
      // MPS-263 KYC-104 — also enforced in the procedure.
      !this.isConsentRequired
    );
  }

  formatFileSize(size?: number | null): string {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  businessTypeName(id: any): string {
    const found = this.businessTypes.find((b) => b.id === Number(id));
    return found ? found.displayName : '-';
  }

  /**
   * Compose a one-line display string from a structured address sub-group,
   * for the read-only Preview tab. Returns '-' when empty.
   */
  addressPreview(groupName: 'registeredAddress' | 'tradingAddress'): string {
    const g: any = this.orgForm.get(groupName)?.value || {};
    const parts = [g.street, g.city, g.region, g.country, g.zipcode]
      .map((p) => (p == null ? '' : String(p).trim()))
      .filter((p) => p.length > 0);
    return parts.length ? parts.join(', ') : '-';
  }

  bankAccountTypeName(id: any): string {
    const found = this.bankAccountTypes.find((b) => b.id === Number(id));
    return found ? found.displayName : '-';
  }

  private extractFileName(url: string | null | undefined): string {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || url;
  }

  /* -------------------- Save / Submit -------------------- */

  async saveAsDraft(): Promise<void> {
    if (this.readOnly) return;
    this.saving = true;
    try {
      // Save Org tab silently if any field has content (getRawValue so the
      // disabled email control is included).
      const o = this.orgForm.getRawValue();
      if (o.organizationName || o.email || o.businessTypeId) {
        await this.api.saveOrgByServicePoToken({
          kycId: this.kycId,
          organizationName: o.organizationName,
          businessTypeId: o.businessTypeId || null,
          registeredAddressParts: o.registeredAddress,
          tradingAddressParts: o.tradingAddress,
          logoUrl: this.logoUrl || null,
          email: o.email,
          phone: o.phone,
          website: o.website,
          businessRegistrationNumber: o.registrationNumber,
          taxId: o.taxId,
          // MPS-263 KYC-101
          countryOfIncorporationId: this.selectedCountryId,
          identifiers: toIdentifierPayload(this.selectedCountryCode, this.identifierValues),
          configVersion: KYC_COUNTRY_CONFIG_VERSION,
          currentStep: this.activeTab.toUpperCase(),
        });
      }

      // Save Banking tab silently if any field has content
      const b = this.bankForm.value;
      if (b.accountHolderName || b.accountNumber) {
        await this.api.saveBankingByServicePoToken({
          kycId: this.kycId,
          accountHolderName: b.accountHolderName,
          bankAccountNumber: b.accountNumber,
          ifscSwiftCode: b.ifscSwift,
          // MPS-263 KYC-102
          bankName: b.bankName,
          branchName: b.branchName,
          bankAccountTypeId: b.accountTypeId || null,
          currentStep: this.activeTab.toUpperCase(),
        });
      }

      this.snackBar.open('Draft saved successfully', 'OK', { duration: 2500 });
      this.saved.emit({ status: 'IN_PROGRESS' });
    } catch (error: any) {
      this.snackBar.open(error?.error?.message || 'Failed to save draft', 'OK', { duration: 3000 });
    } finally {
      this.saving = false;
    }
  }

  async submitKyc(): Promise<void> {
    if (this.readOnly) return;
    if (!this.isFormReadyToSubmit()) {
      this.activeTab = 'preview';
      this.snackBar.open(
        this.describeWhatIsMissing(),
        'OK',
        { duration: 3500 }
      );
      this.orgForm.markAllAsTouched();
      this.bankForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    try {
      // Persist any pending file uploads first, then form data, then submit.
      await this.flushPendingDocuments();
      await this.saveAsDraft();
      await this.api.submitByServicePoToken({
        kycId: this.kycId,
        supplierId: this.supplierId,
        // MPS-263 KYC-104 — which wording was accepted, not merely that
        // something was.
        consentAccepted: this.consentAccepted,
        consentTextVersion: KYC_CONSENT_TEXT_VERSION,
      });
      this.snackBar.open('KYC submitted successfully', 'OK', { duration: 3000 });
      this.saved.emit({ status: 'SUBMITTED' });
      this.close.emit();
    } catch (error: any) {
      this.snackBar.open(error?.error?.message || 'Failed to submit KYC', 'OK', { duration: 3500 });
    } finally {
      this.saving = false;
    }
  }

  onClose(): void {
    this.close.emit();
  }
}

